import { AdminOnlineCourseService, onlineCourseService } from "@onair/services";
import {
	getListOnlineCourseAndComboCacheInput,
	GetOnlineCourseInput,
	GetOnlineCourseLnDQuery,
	GetOnlineCourseV2ListByAdminInput,
	OnlineCourseComboQueryFilter,
	getCourseWithProgress,
} from "@onair/services/online-course";
import {
	useInfiniteQuery,
	QueryClient,
	useQuery,
	infiniteQueryOptions,
	queryOptions,
	FetchQueryOptions,
	UseQueryOptions,
} from "@tanstack/react-query";
import {
	GET_ONLINE_COURSE,
	GET_ONLINE_COURSE_REGISTERED_USERS,
	GET_PREVIEW_LESSONS,
	GET_ONLINE_COURSE_AND_COMBO,
	GET_ONLINE_COURSE_AND_COMBO_CACHE,
	GET_ACADEMY_ONLINE_COURSES,
	GET_ACADEMY_ONLINE_COURSES_FAVORITE,
	ONLINE_COURSE_LIST_BY_ADMIN,
	ONLINE_COURSE_LIST_BY_HOST,
	GET_RESKILL_ONLINE_COURSES_FAVORITE,
	GET_RESKILL_ONLINE_COURSES,
	GET_LAUNCHPAD_ONLINE_COURSES_FAVORITE,
	GET_LAUNCHPAD_ONLINE_COURSES,
	ONLINE_COURSE_DETAIL,
	ONLINE_COURSE_RECOMMENDATION,
	GET_ONLINE_COURSE_V2_DETAILED,
	ONLINE_COURSE_V2_LIST_BY_ADMIN,
	GET_RESOURCES_BY_IDS,
	GET_ONLINE_COURSES_LND,
	GET_COURSE_WITH_PROGRESS,
} from "../../keys";
import { calculateSkip } from "@onair/utils/paging";
import {
	GetResourceResponseDto,
	OrderByEnum,
	restRepository,
	OnlineCourseWithProgressDTO,
} from "@onair/repositories";

export const useGetOnlineCourseList = (input: GetOnlineCourseInput) => {
	const { data: onlineCourses, isLoading } = useQuery({
		queryKey: ["onlineCourseList"],
		queryFn: async () => {
			const response = await onlineCourseService.getListOnlineCourse(input);
			return response.items;
		},
	});
	return {
		onlineCourses,
		loadingOnlineCourse: isLoading,
	};
};

export const useGetOnlineCourseDetail = (slug: string) => {
	const { data: onlinceCourse, isLoading } = useQuery({
		queryKey: ["onlineCourseDetail", slug],
		queryFn: () => onlineCourseService.getOnlineCourseDetail(slug),
	});
	return {
		onlinceCourse,
		loadingOnlineCourse: isLoading,
	};
};

export const useQueryTopOnlineCourseByHost = (hostId?: string) => {
	return useQuery({
		queryKey: ["ONLINE_COURSE_LIST_BY_HOST", hostId],
		queryFn: () =>
			onlineCourseService.getListOnlineCourse({
				limit: 10,
				status: "PUBLISHED",
				orderField: "createdAt",
				order: "DESC",
				userId: hostId,
			}),
	});
};

export const useGetCourseRegisteredUsers = (
	courseId: string,
	page: number,
	limit: number,
	search: string,
	{ isAcademy = false } = {},
) => {
	return useQuery({
		queryKey: [
			GET_ONLINE_COURSE_REGISTERED_USERS,
			courseId,
			page,
			limit,
			isAcademy,
			search,
		],
		queryFn: () =>
			isAcademy
				? onlineCourseService.getCourseRegisteredSubscriptionUsers(courseId, {
						take: limit,
						skip: calculateSkip(page, limit),
						search: search,
					})
				: onlineCourseService.getCourseRegisteredUsers(courseId, {
						take: limit,
						skip: calculateSkip(page, limit),
						search: search,
					}),
		enabled: !!courseId,
	});
};

export const useQueryOnlineCourseDetailById = (id: string) => {
	return useQuery({
		queryKey: [GET_ONLINE_COURSE, id],
		queryFn: () => onlineCourseService.getOnlineCourseDetailById(id),
	});
};

export const useQueryOnlineCourseV2Detailed = (id: string) => {
	return useQuery({
		queryKey: [GET_ONLINE_COURSE_V2_DETAILED, id],
		queryFn: () => onlineCourseService.getOnlineCourseV2Detailed(id),
		enabled: !!id,
	});
};

export const useGetPreviewLessonsByCourseId = (
	id: string,
	{ enabled = true },
) => {
	return useQuery({
		queryKey: [GET_PREVIEW_LESSONS, id],
		queryFn: () => onlineCourseService.getPreviewLessonsByCourseId(id),
		enabled,
	});
};

export const useGetOnlineCoursesAndCombosCache = (options?: {
	query: getListOnlineCourseAndComboCacheInput;
	enabled?: boolean;
}) => {
	const { query, enabled = true } = options || {};
	return useQuery({
		queryKey: [GET_ONLINE_COURSE_AND_COMBO_CACHE],
		queryFn: () => onlineCourseService.getListOnlineCourseAndComboCache(query),
		enabled,
	});
};

export const useGetOnlineCoursesAndCombos = (options?: {
	query: getListOnlineCourseAndComboCacheInput;
	enabled?: boolean;
}) => {
	const { query, enabled = true } = options || {};
	return useQuery({
		queryKey: [GET_ONLINE_COURSE_AND_COMBO],
		queryFn: () => onlineCourseService.getOnlineCoursesAndCombos(query),
		enabled,
	});
};

/**
 * Infinite Online course and combo
 * @param filters
 * @returns
 */

function courseAndComboInfiniteQueryOptions(
	filters: OnlineCourseComboQueryFilter,
) {
	let queryKey: (string | OnlineCourseComboQueryFilter)[] = [
		GET_ONLINE_COURSE_AND_COMBO,
	];
	let filterKeysValid: OnlineCourseComboQueryFilter = {};
	Object.entries(filters).forEach(([key, value]) => {
		if (filters[key as keyof OnlineCourseComboQueryFilter]) {
			filterKeysValid = {
				...filterKeysValid,
				[key]: value,
			};
		}
	});
	queryKey = [
		...queryKey,
		{ ...filterKeysValid, orderField: "paidCount", order: OrderByEnum.DESC },
	];
	return infiniteQueryOptions({
		queryKey,
		initialPageParam: "",
		refetchOnMount: true,
		refetchOnWindowFocus: true,
		queryFn: ({ pageParam }) =>
			onlineCourseService.getOnlineCoursesAndCombosCached({
				...filters,
				orderField: "paidCount",
				order: OrderByEnum.DESC,
				cursor: pageParam || undefined,
			}),
		getNextPageParam: ({ paging }, allPages) => paging.nextCursor,
	});
}

export const useInfiniteOnlineCourseQuery = ({
	filters,
}: {
	filters: OnlineCourseComboQueryFilter;
}) => {
	return useInfiniteQuery({
		...courseAndComboInfiniteQueryOptions(filters),
		placeholderData: (previousData, previousQuery) => previousData,
	});
};

export const usePrefetchInfiniteOnlineCourseQuery = async (
	filters: OnlineCourseComboQueryFilter,
	queryClient: QueryClient,
) => {
	return queryClient.prefetchInfiniteQuery(
		courseAndComboInfiniteQueryOptions(filters),
	);
};

/**
 *
 * @param filters GetOnlineCourseInput
 * @param queryClient
 * @returns Academy course list
 */

function academyCoursesInfiniteQueryOptions(filters: GetOnlineCourseInput) {
	let queryKey: (string | GetOnlineCourseInput)[] = [
		GET_ACADEMY_ONLINE_COURSES,
	];
	let filterKeysValid: GetOnlineCourseInput = {};
	Object.entries(filters).forEach(([key, value]) => {
		if (filters[key as keyof GetOnlineCourseInput]) {
			filterKeysValid = {
				...filterKeysValid,
				[key]: value,
			};
		}
	});
	queryKey = [...queryKey, filterKeysValid];
	return infiniteQueryOptions({
		queryKey,
		initialPageParam: "",
		refetchOnMount: true,
		refetchOnWindowFocus: true,
		queryFn: ({ pageParam }) => {
			return onlineCourseService.getListOnlineCourse({
				...filters,
				//@ts-ignore
				isAcademy: 1,
				cursor: pageParam || filters.cursor || undefined,
			});
		},
		getNextPageParam: ({ paging }, allPages) => paging.nextCursor,
	});
}

function reskillCoursesInfiniteQueryOptions(filters: GetOnlineCourseInput) {
	let queryKey: (string | GetOnlineCourseInput)[] = [
		GET_RESKILL_ONLINE_COURSES,
	];
	let filterKeysValid: GetOnlineCourseInput = {};
	Object.entries(filters).forEach(([key, value]) => {
		if (filters[key as keyof GetOnlineCourseInput]) {
			filterKeysValid = {
				...filterKeysValid,
				[key]: value,
			};
		}
	});
	queryKey = [...queryKey, filterKeysValid];
	return infiniteQueryOptions({
		queryKey,
		initialPageParam: "",
		refetchOnMount: true,
		refetchOnWindowFocus: true,
		queryFn: ({ pageParam }) => {
			return onlineCourseService.getListOnlineCourse({
				...filters,
				//@ts-ignore
				isReskill: 1,
				cursor: pageParam || filters.cursor || undefined,
			});
		},
		getNextPageParam: ({ paging }, allPages) => paging.nextCursor,
	});
}

function launchpadCoursesInfiniteQueryOptions(filters: GetOnlineCourseInput) {
	let queryKey: (string | GetOnlineCourseInput)[] = [
		GET_LAUNCHPAD_ONLINE_COURSES,
	];
	let filterKeysValid: GetOnlineCourseInput = {};
	Object.entries(filters).forEach(([key, value]) => {
		if (filters[key as keyof GetOnlineCourseInput]) {
			filterKeysValid = {
				...filterKeysValid,
				[key]: value,
			};
		}
	});
	queryKey = [...queryKey, filterKeysValid];
	return infiniteQueryOptions({
		queryKey,
		initialPageParam: "",
		refetchOnMount: true,
		refetchOnWindowFocus: true,
		queryFn: ({ pageParam }) => {
			return onlineCourseService.getListOnlineCourse({
				...filters,
				//@ts-ignore
				isLaunchpad: 1,
				cursor: pageParam || filters.cursor || undefined,
			});
		},
		getNextPageParam: ({ paging }, allPages) => paging.nextCursor,
	});
}

export const useAcademyCoursesInfiniteQuery = ({
	filters,
}: {
	filters: GetOnlineCourseInput;
}) => {
	return useInfiniteQuery({
		...academyCoursesInfiniteQueryOptions(filters),
		placeholderData: (previousData, previousQuery) => previousData,
	});
};

export const useReskillCoursesInfiniteQuery = ({
	filters,
}: {
	filters: GetOnlineCourseInput;
}) => {
	return useInfiniteQuery({
		...reskillCoursesInfiniteQueryOptions(filters),
		placeholderData: (previousData, previousQuery) => previousData,
	});
};

export const useLaunchpadCoursesInfiniteQuery = ({
	filters,
}: {
	filters: GetOnlineCourseInput;
}) => {
	return useInfiniteQuery({
		...launchpadCoursesInfiniteQueryOptions(filters),
		placeholderData: (previousData, previousQuery) => previousData,
	});
};

export const usePrefetchAcademyCoursesInfiniteQuery = (
	queryClient: QueryClient,
	filters: GetOnlineCourseInput,
) => {
	return queryClient.prefetchInfiniteQuery(
		academyCoursesInfiniteQueryOptions(filters),
	);
};

export const usePrefetchReskillCoursesInfiniteQuery = (
	queryClient: QueryClient,
	filters: GetOnlineCourseInput,
) => {
	return queryClient.prefetchInfiniteQuery(
		reskillCoursesInfiniteQueryOptions(filters),
	);
};

export const usePrefetchLaunchpadCoursesInfiniteQuery = (
	queryClient: QueryClient,
	filters: GetOnlineCourseInput,
) => {
	return queryClient.prefetchInfiniteQuery(
		launchpadCoursesInfiniteQueryOptions(filters),
	);
};

/**
 *
 * @param filters
 * @returns Academy favorite courses
 */
const academyCoursesQueryOptions = (filters: GetOnlineCourseInput) => {
	return queryOptions({
		queryKey: [GET_ACADEMY_ONLINE_COURSES_FAVORITE, filters],
		queryFn: () =>
			onlineCourseService.getListOnlineCourse({
				...filters,
				//@ts-ignore
				isAcademy: 1,
				order: "DESC",
				orderBy: "product.paidCount",
			}),
	});
};

const reskillCoursesQueryOptions = (filters: GetOnlineCourseInput) => {
	return queryOptions({
		queryKey: [GET_RESKILL_ONLINE_COURSES_FAVORITE, filters],
		queryFn: () =>
			onlineCourseService.getListOnlineCourse({
				...filters,
				//@ts-ignore
				isReskill: 1,
				order: "DESC",
				orderBy: "product.paidCount",
			}),
	});
};

const launchpadCoursesQueryOptions = (filters: GetOnlineCourseInput) => {
	return queryOptions({
		queryKey: [GET_LAUNCHPAD_ONLINE_COURSES_FAVORITE, filters],
		queryFn: () =>
			onlineCourseService.getListOnlineCourse({
				...filters,
				//@ts-ignore
				isLaunchpad: 1,
				order: "DESC",
				orderBy: "product.paidCount",
			}),
	});
};

export const usePrefetchAcademyCoursesFavoriteQuery = (
	queryClient: QueryClient,
	filters: GetOnlineCourseInput,
) => {
	return queryClient.prefetchQuery(academyCoursesQueryOptions(filters));
};

export const usePrefetchReskillCoursesFavoriteQuery = (
	queryClient: QueryClient,
	filters: GetOnlineCourseInput,
) => {
	return queryClient.prefetchQuery(reskillCoursesQueryOptions(filters));
};

export const usePrefetchLaunchpadCoursesFavoriteQuery = (
	queryClient: QueryClient,
	filters: GetOnlineCourseInput,
) => {
	return queryClient.prefetchQuery(launchpadCoursesQueryOptions(filters));
};

export const useAcademyCoursesFavoriteQuery = (
	filters: GetOnlineCourseInput,
) => {
	return useQuery(academyCoursesQueryOptions(filters));
};

export const useReskillCoursesFavoriteQuery = (
	filters: GetOnlineCourseInput,
) => {
	return useQuery(reskillCoursesQueryOptions(filters));
};

export const useLaunchpadCoursesFavoriteQuery = (
	filters: GetOnlineCourseInput,
) => {
	return useQuery(launchpadCoursesQueryOptions(filters));
};

export const useGetInfinitiveOnlineCourseList = ({
	isAdmin,
	enabled,
	filters,
}: {
	isAdmin: boolean;
	enabled?: boolean;
	filters: {
		limit: number;
		filter?: string;
		status?: string;
		fromDate?: string;
		toDate?: string;
		orderBy?: string;
		order?: "ASC" | "DESC";
	};
}) => {
	return useInfiniteQuery({
		queryKey: [
			isAdmin ? ONLINE_COURSE_LIST_BY_ADMIN : ONLINE_COURSE_LIST_BY_HOST,
			filters,
		],
		initialPageParam: 1,
		queryFn: ({ pageParam = 1 }) => {
			const payload = {
				...filters,
				offset: pageParam,
			};
			return isAdmin
				? AdminOnlineCourseService.getListOnlineCourseByAdmin(payload)
				: onlineCourseService.getListOnlineCourseOwnedByHost(payload);
		},
		getNextPageParam: (lastPage, allPages) =>
			lastPage.paging.hasNextPage ? allPages.length + 1 : undefined,
		select: (data) => ({
			pages: data.pages,
			pageParams: data.pageParams,
			items: data.pages.flatMap((page) => page.items),
		}),
		staleTime: 0, // Make sure data is always considered stale
		cacheTime: 0, // Disable caching
		refetchOnMount: true, // Refetch when component mounts
		refetchOnWindowFocus: true, // Refetch when window gains focus
		// enabled,
	});
};

export const useOnlineCourseDeepDetail = (
	slug: string,
	options = { enabled: true },
) => {
	return useQuery({
		queryKey: [ONLINE_COURSE_DETAIL, slug],
		queryFn: () => onlineCourseService.getDeepOnlineCourseDetail(slug),
		enabled: options.enabled,
	});
};

export const useGetOnlineCoursesReskillQuery = (
	filters: GetOnlineCourseInput,
	options?: { enabled?: boolean },
) => {
	const { enabled = true } = options || {};
	return useQuery({
		queryKey: [GET_RESKILL_ONLINE_COURSES, filters],
		queryFn: () =>
			onlineCourseService.getListOnlineCourse({
				...filters,
				//@ts-ignore
				isReskill: 1,
				order: "DESC",
				orderBy: "product.paidCount",
			}),
		enabled,
	});
};

export const useGetOnlineCoursesCareerLaunchpadQuery = (
	filters: GetOnlineCourseInput,
	options?: { enabled?: boolean },
) => {
	const { enabled = true } = options || {};
	return useQuery({
		queryKey: [GET_LAUNCHPAD_ONLINE_COURSES, filters],
		queryFn: () =>
			onlineCourseService.getListOnlineCourse({
				...filters,
				//@ts-ignore
				isLaunchpad: 1,
				order: "DESC",
				orderBy: "product.paidCount",
			}),
		enabled,
	});
};

export const useGetOnlineCoursesForLnDQuery = (
	filters: GetOnlineCourseInput,
	options?: { enabled?: boolean },
) => {
	const { enabled = true } = options || {};
	return useQuery({
		queryKey: [GET_ONLINE_COURSE, filters],
		queryFn: () =>
			onlineCourseService.getListOnlineCourse({
				...filters,
				isLnD: 0,
				isFree: true,
			}),
		enabled,
	});
};

export const useQueryResourcesByIds = (ids: string[], options = {}) => {
	return useQuery({
		...options,
		queryKey: [GET_RESOURCES_BY_IDS, ids],
		queryFn: async () => {
			if (ids) {
				const request = ids.map((id) => restRepository.api.resourceGetById(id));
				const response = await Promise.all(request);
				return response.map((res) => res.data) as GetResourceResponseDto[];
			}
		},
		enabled: !!ids && !!ids.length,
	});
};

export const useGetOnlineCourseV2ListByAdmin = (
	input: GetOnlineCourseV2ListByAdminInput,
	options?: { enabled?: boolean },
) => {
	const { enabled = true } = options || {};
	return useQuery({
		queryKey: [ONLINE_COURSE_V2_LIST_BY_ADMIN, input],
		queryFn: () => onlineCourseService.getOnlineCourseV2ListByAdmin(input),
		enabled,
	});
};

export const useGetCourseRecommendation = (slug: string, isLnD?: boolean) => {
	return useQuery({
		queryKey: [ONLINE_COURSE_RECOMMENDATION, slug, isLnD],
		queryFn: () => onlineCourseService.getCourseRecommendation(slug, isLnD),
		enabled: Boolean(slug),
	});
};

/**
 * Course list LND
 */

const lndOnlineCoursesQueryOptions = (filter?: GetOnlineCourseLnDQuery) => {
	return queryOptions({
		queryKey: [GET_ONLINE_COURSES_LND, filter],
		queryFn: () => onlineCourseService.getOnlineCousesLnD(filter),
	});
};

const lndOnlineCoursesInfiniteQueryOptions = (
	filters: GetOnlineCourseLnDQuery,
) => {
	let queryKey: (string | GetOnlineCourseLnDQuery)[] = [GET_ONLINE_COURSES_LND];
	let filterKeysValid: GetOnlineCourseLnDQuery = {};
	Object.entries(filters).forEach(([key, value]) => {
		if (filters[key as keyof GetOnlineCourseLnDQuery]) {
			filterKeysValid = {
				...filterKeysValid,
				[key]: value,
			};
		}
	});
	queryKey = [
		...queryKey,
		{ ...filterKeysValid, orderField: "paidCount", order: OrderByEnum.DESC },
	];
	return infiniteQueryOptions({
		queryKey,
		initialPageParam: filters.offset ?? 1,
		refetchOnMount: true,
		refetchOnWindowFocus: true,
		queryFn: ({ pageParam }) =>
			onlineCourseService.getOnlineCousesLnD({
				...filters,
				orderField: "paidCount",
				order: OrderByEnum.DESC,
				offset: pageParam,
			}),
		getNextPageParam: ({ paging }, allPages) =>
			paging.pageCount > paging.offset ? paging.offset + 1 : null,
	});
};

export const useGetOnlineCourseLNDInfiniteQuery = ({
	filters,
}: {
	filters: GetOnlineCourseLnDQuery;
}) => {
	return useInfiniteQuery({
		...lndOnlineCoursesInfiniteQueryOptions(filters),
		placeholderData: (previousData, previousQuery) => previousData,
	});
};

export const prefetchOnlineCourseLNDQuery = (
	queryClient: QueryClient,
	filter?: GetOnlineCourseLnDQuery,
) => {
	return queryClient.prefetchQuery(lndOnlineCoursesQueryOptions(filter));
};

export const prefetchOnlineCourseLNDInfiniteQuery = (
	queryClient: QueryClient,
	filter: GetOnlineCourseLnDQuery,
) => {
	return queryClient.prefetchInfiniteQuery(
		lndOnlineCoursesInfiniteQueryOptions(filter),
	);
};

export const useGetOnlineCourseLNDQuery = (
	filter?: GetOnlineCourseLnDQuery,
) => {
	return useQuery(lndOnlineCoursesQueryOptions(filter));
};

export const useQueryCourseWithProgress = (
	slug: string,
	options?: UseQueryOptions<OnlineCourseWithProgressDTO, Error>
) => {
	return useQuery({
		queryKey: [GET_COURSE_WITH_PROGRESS, slug],
		queryFn: () => getCourseWithProgress(slug),
		enabled: !!slug,
		...options,
	});
};
